import type { PlayerAIChatMessage } from "./playerai.types";

/** One entry in the rendered thread: a row, or a folded stretch of others'. */
export type ThreadEntry =
  | { readonly kind: "row"; readonly message: PlayerAIChatMessage }
  | {
      readonly kind: "hidden";
      /** Keyed by the first hidden row, so expansion survives new arrivals. */
      readonly key: string;
      readonly rows: readonly PlayerAIChatMessage[];
      readonly humanCount: number;
      readonly aiCount: number;
    };

export type HiddenThreadEntry = Extract<ThreadEntry, { kind: "hidden" }>;

/** The folded stretch that holds a message, when one does. */
export function hiddenEntryHolding(
  entries: readonly ThreadEntry[],
  messageId: string,
): HiddenThreadEntry | undefined {
  for (const entry of entries) {
    if (
      entry.kind === "hidden" &&
      entry.rows.some((row) => row.id === messageId)
    ) {
      return entry;
    }
  }
  return undefined;
}

/**
 * Task 17 — "My exchanges only" as a pure presentation fold.
 *
 * Every contiguous stretch of rows the host marked `exchange: "other"` becomes
 * one disclosure entry, in place, so chronology is untouched. Rows without a
 * marking (notices, failures, live activity) always show. Off, the thread is
 * returned row for row.
 */
export function foldHiddenExchanges(
  rows: readonly PlayerAIChatMessage[],
  enabled: boolean,
): ThreadEntry[] {
  if (!enabled) return rows.map((message) => ({ kind: "row", message }));
  const entries: ThreadEntry[] = [];
  let hidden: PlayerAIChatMessage[] = [];
  const flush = (): void => {
    if (hidden.length === 0) return;
    entries.push({
      kind: "hidden",
      key: `hidden-${hidden[0]!.id}`,
      rows: hidden,
      humanCount: hidden.filter((row) => row.exchangeCountAs === "human")
        .length,
      aiCount: hidden.filter((row) => row.exchangeCountAs === "ai").length,
    });
    hidden = [];
  };
  for (const message of rows) {
    if (message.exchange === "other") {
      hidden.push(message);
      continue;
    }
    flush();
    entries.push({ kind: "row", message });
  }
  flush();
  return entries;
}

/**
 * "2 messages from others · 1 Player AI response". Only hidden human messages
 * and hidden Player AI responses are counted — never proposals, status events
 * or streaming chunks — and a zero-count category is left out.
 */
export function hiddenExchangeSummary(
  humanCount: number,
  aiCount: number,
): string {
  const parts: string[] = [];
  if (humanCount > 0) {
    parts.push(
      humanCount === 1
        ? "1 message from others"
        : `${humanCount} messages from others`,
    );
  }
  if (aiCount > 0) {
    parts.push(
      aiCount === 1 ? "1 Player AI response" : `${aiCount} Player AI responses`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : "Other activity";
}
